import { useState } from 'react'
import { useFormikContext } from 'formik'
import { values, map, prop, isEmpty, pipe, concat, path } from 'ramda'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import { useDialogController } from '@machobear/design-system'
import Icon from '@/components/icon'
import { showTenantModalOptions } from '@/components/tenant/tenant-modals'
import useCurrentSession from '@/hooks/use-current-session'
import ListingUsersContainer from '@/features/Listing/landlord-container'
import { isNullOrEmpty } from '@/utils'
import { select } from '@/store'
import { colors, fontWeights, breakpoints, fontSizes } from '@/styles'
import UserPicker from '@/components/user-picker'
import Highlight from './tour'
import { Wrapper, SectionTitle, SectionSubtitle } from '../elements'

const AddUserButton = styled.button`
  height: 66px;
  width: 100%;
  max-width: 325px;
  border-radius: 6px;
  border: 1px solid ${colors.grey};
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${colors.regular};
  font-size: ${fontSizes.regular};
  font-weight: ${fontWeights.medium};
  line-height: 22px;
  padding: 20px;
  background-color: transparent;
  cursor: pointer;
`

const Section = styled.section`
  display: flex;

  @media only screen and (max-width: ${breakpoints.ipadMiniMax}) {
    flex-direction: column;
  }
`

const SubSection = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;

  > * {
    margin-bottom: 20px;
  }

  :not(:last-child) {
    padding-right: 20px;
  }

  @media only screen and (max-width: ${breakpoints.ipadMiniMax}) {
    :not(:last-child) {
      padding: 0;
      margin-bottom: 30px;
    }
  }
`

const ContractParties = ({ listingId, contractId }) => {
  const [addOpen, setAddOpen] = useState()
  const { t } = useTranslation()
  const { setFieldValue, values: data } = useFormikContext()
  const { visible, showModal, closeModal } = useDialogController()
  const [isExtraUser, setIsExtraUser] = useState(false)
  const [currentDialogType, setCurrentDialogType] = useState('')

  const allTenants = useSelector(select.listing.applicants)
  const session = useCurrentSession()
  const broker = session?.company_info?.broker
  const isPersonalAccount = session?.account_type === 'personal'
  const allowMultiple = isNullOrEmpty(broker) && !isPersonalAccount

  const allLandlords = useSelector(state =>
    select.listing.landlords(state, {
      listingId
    })
  )
  const contractType = useSelector(path(['contract', contractId, 'contract_type']))
  const isExtension = contractType === 'extension'

  const handleDeleteLandlord = landlord => {
    setFieldValue(
      'contract_landlords',
      data.contract_landlords.filter(l => l.id !== landlord.id)
    )
  }

  const handleDeleteTenant = tenant => {
    const signing_ids = pipe(map(prop('id')), values)(data.contract_signing_tenants)

    if (signing_ids.includes(tenant.id)) {
      setFieldValue(
        'contract_signing_tenants',
        data.contract_signing_tenants.filter(obj => obj.id !== tenant.id)
      )
    } else {
      setFieldValue(
        'contract_extra_users',
        data.contract_extra_users.filter(obj => obj.id !== tenant.id)
      )
    }

    if (!tenant.signature_required) {
      setFieldValue(
        'contract_non_signing_tenants',
        data.contract_non_signing_tenants.filter(obj => obj.id !== tenant.id)
      )
    }
  }

  const handleSelectedLandlords = landlords => {
    setFieldValue('contract_landlords', landlords)
  }

  const renderLandlords = () =>
    data?.contract_landlords?.length > 0
      ? data.contract_landlords.map(landlord => (
          <ListingUsersContainer
            data-testid={`landlord-${landlord.id}`}
            key={landlord.id}
            user={landlord}
            showPrimary={false}
            handleDelete={() => handleDeleteLandlord(landlord)}
          />
        ))
      : null

  const renderTenants = tenants => {
    return tenants?.length > 0
      ? tenants.map(
          (tenant, index) =>
            !isEmpty(tenant) && (
              <ListingUsersContainer
                key={`${tenant?.first_name} - ${index}`}
                user={tenant}
                showPrimary={false}
                handleDelete={() => handleDeleteTenant(tenant)}
              />
            )
        )
      : null
  }

  return (
    <>
      <Wrapper>
        <SectionTitle>{t('l.contract.create.the_tenants.title', 'The Tenants')}</SectionTitle>
        <Section>
          <SubSection>
            <SectionSubtitle>
              {t('l.contract.create.signing_tenants.subtitle', 'Signing Tenant')}
            </SectionSubtitle>
            {renderTenants(data.contract_signing_tenants)}
            <Highlight hide={addOpen}>
              <AddUserButton
                id="add-user-btn"
                type="button"
                onClick={() => {
                  setAddOpen(true)
                  setCurrentDialogType('Tenant')
                  setIsExtraUser(false)
                  showTenantModalOptions({
                    listingId: listingId,
                    allTenants: allTenants,
                    data,
                    disabledUsers: data.contract_non_signing_tenants,
                    onAddNewUser: tenants => setFieldValue('contract_signing_tenants', tenants),
                    isExtension,
                    showModal
                  })
                }}
              >
                {t('l.contract.create.add_tenant.button', 'Add a tenant')}
                <Icon id="plus" height={18} width={18} />
              </AddUserButton>
            </Highlight>
          </SubSection>
          <SubSection>
            <SectionSubtitle>
              {t('l.contract.create.non_signing_tenants.subtitle', 'Non-Signing Tenant (optional)')}
            </SectionSubtitle>
            {renderTenants(concat(data.contract_extra_users, data.contract_non_signing_tenants))}
            <AddUserButton
              type="button"
              onClick={() => {
                setCurrentDialogType('Tenant')
                setIsExtraUser(true)
                showTenantModalOptions({
                  listingId: listingId,
                  allTenants: allTenants,
                  data,
                  isExtraUser: true,
                  onAddNewUser: tenants => setFieldValue('contract_extra_users', tenants),
                  isExtension,
                  showModal: showModal
                })
              }}
            >
              {t('l.contract.create.add_tenant.button', 'Add a tenant')}
              <Icon id="plus" height={18} width={18} />
            </AddUserButton>
          </SubSection>
        </Section>
        <SectionTitle>
          {t('l.contract.create.the_landlords.subtitle', 'The Landlords / Agents')}
        </SectionTitle>
        <Section>
          <SubSection>
            {renderLandlords()}
            {(data?.contract_landlords?.length === 0 || allowMultiple) && (
              <AddUserButton
                type="button"
                onClick={() => {
                  setCurrentDialogType('Landlord')
                  showModal()
                }}
              >
                {t('l.contract.create.add_landlord.button', 'Add a landlord')}
                <Icon id="plus" height={18} width={18} />
              </AddUserButton>
            )}
          </SubSection>
        </Section>
      </Wrapper>
      <UserPicker
        title={currentDialogType === 'Tenant' ? 'Select Tenant' : 'Select Landlords'}
        visible={visible}
        onClose={closeModal}
        allUsers={
          currentDialogType === 'Tenant' ? allTenants : allLandlords.length > 0 ? allLandlords : []
        }
        activeUsers={
          currentDialogType === 'Tenant'
            ? data[isExtraUser ? 'contract_non_signing_tenants' : 'contract_signing_tenants']
            : data.contract_landlords
        }
        disabledUsers={
          currentDialogType === 'Tenant'
            ? concat(data.contract_non_signing_tenants, data.contract_signing_tenants)
            : undefined
        }
        setActiveUsers={
          currentDialogType === 'Tenant'
            ? isExtraUser
              ? tenants => setFieldValue('contract_non_signing_tenants', tenants)
              : tenants => setFieldValue('contract_signing_tenants', tenants)
            : handleSelectedLandlords
        }
        maxSelect={currentDialogType === 'Tenant' || allowMultiple ? undefined : 1}
        showLandlordAlerts={currentDialogType === 'Landlord'}
        alertTrackingId="app_upgrade_createcontract_addlandlord_anchortext"
      />
    </>
  )
}

export default ContractParties
